// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — descriptores del modelo BPMM (prompt 0735)
// Fuente única de los DESCRIPTORES de nivel del diagnóstico de procesos: 5 niveles
// BPMM (Robledo) + matriz 7 pilares × 5 niveles, anclada a la lectura REAL que mina
// el mapa de procesos (% de procesos con cada atributo). Modelo VALOR·IA de Madurez de
// Procesos (base: BPMM Robledo 2014 + PEMM Hammer 2007) — ver ADR-038. Contenido (ES): lo
// consume el heatmap (tooltips + leyenda). Espejo de descriptores-madurez.ts (M1.5).
// ════════════════════════════════════════════════════════════════════════════
import type { PilarBpmm, NivelBpmm } from './api-madurez-proceso';

export interface NivelInfoBpmm {
  nivel: NivelBpmm;
  /** Etiqueta corta N1…N5 para mostrar. */
  etiqueta: string;
  nombre: string;
  /** Significado general (independiente del pilar). */
  resumen: string;
  /** Banda de color del modelo: 🟢 N4/N5 · 🟡 N2/N3 · 🔴 N1. */
  color: 'VERDE' | 'AMARILLO' | 'ROJO';
}

/**
 * Los 5 niveles BPMM de Pedro Robledo (2014), nomenclatura CANÓNICA (matriz Excel
 * Robledo · adoptada en 0742): Inicial · Gestionado · Definido · Orientación por
 * Proceso · Excelencia. (Antes teníamos Definido/Gestionado invertidos y Optimizado/
 * Innovador genéricos.) Global = media de pilares (no eslabón débil, eso es PEMM).
 */
export const NIVELES_BPMM: NivelInfoBpmm[] = [
  {
    nivel: 'N1',
    etiqueta: 'N1',
    nombre: 'Inicial',
    resumen:
      'Inicial: sin alineamiento de los procesos a la estrategia; gestión ad-hoc, no se mide ni documenta de forma fiable.',
    color: 'ROJO',
  },
  {
    nivel: 'N2',
    etiqueta: 'N2',
    nombre: 'Gestionado',
    resumen:
      'Gestionado: algunas métricas/KPIs del rendimiento de los procesos y primeras estrategias de mejora; cierta involucración de la dirección.',
    color: 'AMARILLO',
  },
  {
    nivel: 'N3',
    etiqueta: 'N3',
    nombre: 'Definido',
    resumen:
      'Definido: mapa de procesos, estrategia BPM y roadmap definidos; cuadros de mando de procesos enlazados con la estrategia.',
    color: 'AMARILLO',
  },
  {
    nivel: 'N4',
    etiqueta: 'N4',
    nombre: 'Orientación por Proceso',
    resumen:
      'Orientación por proceso: automatización extendida y cuadros de mando estratégicos y operativos con respuesta operativa ágil.',
    color: 'VERDE',
  },
  {
    nivel: 'N5',
    etiqueta: 'N5',
    nombre: 'Excelencia',
    resumen:
      'Excelencia: gestión predictiva, proactiva y reactiva en tiempo real; mejora continua y alineación con todas las gestiones empresariales.',
    color: 'VERDE',
  },
];

export const NIVEL_INFO_BPMM: Record<NivelBpmm, NivelInfoBpmm> = Object.fromEntries(
  NIVELES_BPMM.map((n) => [n.nivel, n]),
) as Record<NivelBpmm, NivelInfoBpmm>;

/**
 * Marco teórico que sustenta cada pilar. El modelo canónico es BPMM-Robledo como
 * espina dorsal (7 pilares + niveles 1-5) injertado con las capacidades de empresa
 * de Hammer (PEMM-Empresa: Liderazgo, Cultura, Gobierno) y sus habilitadores de
 * proceso (Diseño, Ejecutores, Propietario, Infraestructura, Indicadores). Ver ADR-038.
 */
export const MARCO_PILAR: Record<PilarBpmm, string> = {
  ESTRATEGIA:
    'Robledo (BPMM) — pilar Estrategia · Hammer (PEMM-Empresa): Liderazgo y despliegue de la estrategia al proceso.',
  PROCESOS: 'Robledo (BPMM) — pilar Procesos · Hammer (PEMM): habilitador Diseño del proceso.',
  METODO:
    'Robledo (BPMM) — pilar Método · Hammer (PEMM): Diseño y documentación normalizada del proceso.',
  TECNOLOGIA:
    'Robledo (BPMM) — pilar Tecnología · Hammer (PEMM): Infraestructura (sistemas de información de soporte).',
  PERSONAS:
    'Robledo (BPMM) — pilar Personas · Hammer (PEMM): Ejecutores y Propietario (roles y competencias).',
  GOBIERNO:
    'Robledo (BPMM) — pilar Gobierno · Hammer (PEMM-Empresa): Gobierno + habilitadores Propietario e Indicadores.',
  CULTURA:
    'Robledo (BPMM) — pilar Cultura · Hammer (PEMM-Empresa): Cultura (trabajo en equipo, orientación al cliente, responsabilidad).',
};

/** Nota de método (común a los 7 pilares). */
export const METODO_GLOBAL_BPMM =
  'Nivel global BPMM = media de los 7 pilares con dato (método de Robledo 2014: radar + media 1-5). El eslabón débil (MIN, Hammer/PEMM) se reserva para la capa PEMM por proceso.';

/** La pregunta de gobernanza que evalúa cada pilar (cabecera del tooltip). */
export const PREGUNTA_PILAR: Record<PilarBpmm, string> = {
  ESTRATEGIA: '¿Los procesos despliegan la estrategia (cadena objetivo → proceso → KPI)?',
  PROCESOS: '¿Están los procesos identificados, caracterizados y secuenciados?',
  METODO: '¿Está el método de cada proceso documentado y normalizado?',
  TECNOLOGIA: '¿Hay tecnología que soporta y trazabiliza los procesos?',
  PERSONAS: '¿Tienen los procesos responsables y competencias asignadas?',
  GOBIERNO: '¿Se gobiernan los procesos (propietario, indicadores, órganos de revisión)?',
  CULTURA: '¿Existe orientación y cultura de procesos en la organización?',
};

/**
 * Matriz 7×5: qué significa cada nivel EN CADA pilar, en lenguaje de negocio, anclada
 * a los umbrales de la lectura REAL (% de procesos del mapa con cada atributo). N1 es
 * el suelo: proceso ad-hoc o atributo por debajo del mínimo medible. CULTURA no es
 * minable del mapa (no deja huella documental): solo se lee por instrumento cualitativo.
 */
export const DESCRIPTORES_PILAR: Record<PilarBpmm, Record<NivelBpmm, string>> = {
  ESTRATEGIA: {
    N1: 'Procesos sin vínculo estratégico: <20% ligado a un objetivo o KPI corporativo.',
    N2: 'Vínculo incipiente: algunos procesos (≥20%) referencian un objetivo, sin cadena objetivo→KPI.',
    N3: 'Despliegue parcial: la mitad de los procesos (≥50%) con objetivo y métrica asociada.',
    N4: 'Estrategia desplegada: la mayoría (≥80%) con la cadena objetivo→proceso→KPI trazada.',
    N5: 'Alineación total: prácticamente todo el mapa (≥95%) desplegando la estrategia con metas.',
  },
  PROCESOS: {
    N1: 'Mapa incompleto: <20% de los procesos identificados o caracterizados.',
    N2: 'Inventario básico: procesos listados; ≥20% caracterizados.',
    N3: 'Caracterización extendida: ≥50% con ficha y secuencia definida.',
    N4: 'Mapa consolidado: ≥80% caracterizados e interrelacionados.',
    N5: 'Mapa completo: ≥95% identificados, caracterizados y secuenciados en fuente única.',
  },
  METODO: {
    N1: 'Sin método documentado: <20% con flujograma o procedimiento.',
    N2: 'Documentación básica: ≥20% con flujograma o ficha de método.',
    N3: 'Método definido: ≥50% con procedimiento normalizado.',
    N4: 'Método extendido: ≥80% con el diseño documentado y normalizado.',
    N5: 'Método total: ≥95% con procedimiento, flujograma y normalización versionada.',
  },
  TECNOLOGIA: {
    N1: 'Soporte no trazado: <20% de procesos con herramienta o sistema vinculado.',
    N2: 'Digitalización incipiente: ≥20% con alguna herramienta registrada.',
    N3: 'Soporte parcial: ≥50% con sistema de apoyo identificado.',
    N4: 'Soporte sólido: ≥80% con tecnología integrada al proceso.',
    N5: 'Soporte total: ≥95% digitalizado e instrumentado (datos de proceso disponibles).',
  },
  PERSONAS: {
    N1: 'Sin responsables: <20% de procesos con responsable o involucrados asignados.',
    N2: 'Asignación básica: ≥20% con responsable identificado.',
    N3: 'Asignación extendida: ≥50% con responsable e involucrados (RACI parcial).',
    N4: 'Roles consolidados: ≥80% con la responsabilidad de proceso asignada.',
    N5: 'Roles totales: ≥95% con responsable, involucrados y competencias definidas.',
  },
  GOBIERNO: {
    N1: 'Sin gobierno: <20% con propietario de proceso, indicador de gobierno u órgano de revisión.',
    N2: 'Gobierno incipiente: ≥20% con propietario o indicador básico.',
    N3: 'Gobierno definido: ≥50% con propietario e indicador de seguimiento.',
    N4: 'Gobierno activo: ≥80% con propietario, KPI de gobierno y ciclo de revisión.',
    N5: 'Gobierno maduro: ≥95% gobernado con órganos, KPIs y mejora continua institucionalizada.',
  },
  CULTURA: {
    N1: 'Sin señal: la cultura de procesos no deja huella en el mapa; suelo por defecto, no medición.',
    N2: 'Orientación incipiente (solo medible por instrumento cualitativo).',
    N3: 'Orientación a procesos parcial (vía encuesta o entrevistas).',
    N4: 'Cultura de procesos extendida (medida cualitativamente).',
    N5: 'Cultura de mejora continua interiorizada (medida y sostenida en el tiempo).',
  },
};

/** Descriptor de una celda (pilar × nivel). */
export function descriptorCeldaProceso(pilar: PilarBpmm, nivel: NivelBpmm): string {
  return DESCRIPTORES_PILAR[pilar]?.[nivel] ?? '';
}
